import { useFeatureGate } from '@statsig/react-bindings';
import { millisecondsToSeconds, minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { TFunction } from 'i18next';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';
import { useInterval } from 'usehooks-ts';

import { useTranslate } from '@growtherapy/i18n';

import { VISITOR_ACCESS_DOC_TTL_MS } from '../../config';
import { waitingRoomTimeoutGate } from '../../statsig/gates';
import { toast } from '../../toasts';
import { memoize } from '../../utils';
import { VisitorState, visitorNameAtom, visitorStateAtom } from '../state';
import { useResetUserStateCallback } from './useResetUserStateCallback';

// Minutes before the access doc expires
const WAITING_ROOM_TIMEOUT_MS = VISITOR_ACCESS_DOC_TTL_MS - minutesToMilliseconds(5);

// Seconds until the user is shown an informational toast about the timeout
const MS_UNTIL_TIMEOUT_TOAST_IS_SHOWN = secondsToMilliseconds(10);

const WAITING_ROOM_ENTRY_TIME_KEY = 'waiting-room-entry-time';
const TOAST_KEY = 'waiting-room-timeout-toast';

function getWaitingRoomEntryTime() {
  return parseInt(sessionStorage.getItem(WAITING_ROOM_ENTRY_TIME_KEY) ?? '0');
}

function setWaitingRoomEntryTime(timeMs: number) {
  sessionStorage.setItem(WAITING_ROOM_ENTRY_TIME_KEY, timeMs.toString());
}

function clearWaitingRoomEntryTime() {
  sessionStorage.removeItem(WAITING_ROOM_ENTRY_TIME_KEY);
}

function shouldTimeOut(entryTimeMs: number) {
  return Date.now() - entryTimeMs > WAITING_ROOM_TIMEOUT_MS;
}

function shouldShowTimeoutToast(entryTimeMs: number) {
  return Date.now() - entryTimeMs > WAITING_ROOM_TIMEOUT_MS - MS_UNTIL_TIMEOUT_TOAST_IS_SHOWN;
}

const showTimeoutToast = memoize((t: TFunction, _id: number) => {
  toast.info(
    t('telehealth-core:waitingRoom.reachedMaxAllottedTimeRedirectInSeconds', {
      seconds: millisecondsToSeconds(MS_UNTIL_TIMEOUT_TOAST_IS_SHOWN),
    }),
    { key: TOAST_KEY, timeout: Infinity },
  );
});

export function useWaitingRoomTimeout() {
  const t = useTranslate();
  const visitorName = useAtomValue(visitorNameAtom);
  const getVisitorState = useAtomCallback(useCallback((get) => get(visitorStateAtom), []));
  const resetUserState = useResetUserStateCallback();
  const { value: isRejoinPageEnabled } = useFeatureGate(waitingRoomTimeoutGate);

  useEffect(function initEntryTime() {
    setWaitingRoomEntryTime(Date.now());
    return clearWaitingRoomEntryTime;
  }, []);

  useInterval(
    useCallback(
      function handleWaitingRoomTimeout() {
        const isLoadingIntoMeeting = new Set([VisitorState.LOADING, VisitorState.JOINING]).has(getVisitorState());
        if (isLoadingIntoMeeting) return;

        const entryTimeMs = getWaitingRoomEntryTime();
        const timeoutRedirectState = isRejoinPageEnabled
          ? { state: VisitorState.TIMEOUT, name: visitorName }
          : { state: VisitorState.INTRO, name: '' };

        if (!entryTimeMs) return;

        if (shouldTimeOut(entryTimeMs)) {
          toast.close(TOAST_KEY);
          resetUserState(timeoutRedirectState);
          return;
        }

        if (shouldShowTimeoutToast(entryTimeMs)) {
          showTimeoutToast(t, entryTimeMs);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    250,
  );
}
